/**
 * Phase B.4 — render the relationship dynamics block on ContactDetail.
 *
 * Four honest narrow signals:
 *   1. Cadence — how often you talk, when you last spoke
 *   2. Depth — time spent, signals learned
 *   3. Tone — dominant sentiment, distribution
 *   4. Trajectory — warming / cooling / steady
 *
 * Everything is derived from existing data by
 * `buildRelationshipDynamics` — no new LLM calls. The block hides
 * itself when the relationship is too new to produce a meaningful
 * read (empty interactions) so it never shows a confidently-wrong
 * claim on a fresh contact.
 */
import { useEffect, useMemo, useState } from "react";
import { CalendarClock, Clock3, Flame, Leaf, TrendingDown, TrendingUp } from "lucide-react";

import type { ContactSignal, ProcessedInteraction } from "@/types/voice";
import {
  listInteractionsForContact,
  listSignalsForContact,
} from "@/hooks/useSessionProcessor";
import {
  buildRelationshipDynamics,
  type CadenceLabel,
  type TrajectoryLabel,
} from "@/utils/relationshipDynamics/relationshipDynamics";

interface Props {
  contactId: string | null | undefined;
  firstName: string;
}

export function RelationshipDynamics({ contactId, firstName }: Props) {
  const [interactions, setInteractions] = useState<ProcessedInteraction[] | null>(
    null
  );
  const [signals, setSignals] = useState<ContactSignal[] | null>(null);

  useEffect(() => {
    if (!contactId) {
      setInteractions([]);
      setSignals([]);
      return;
    }
    let cancelled = false;
    Promise.all([
      listInteractionsForContact(contactId),
      listSignalsForContact(contactId),
    ])
      .then(([i, s]) => {
        if (cancelled) return;
        setInteractions(i);
        setSignals(s);
      })
      .catch(() => {
        if (cancelled) return;
        setInteractions([]);
        setSignals([]);
      });
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  const dynamics = useMemo(() => {
    if (!interactions || !signals) return null;
    return buildRelationshipDynamics(interactions, signals, new Date());
  }, [interactions, signals]);

  if (!dynamics || dynamics.isEmpty) return null;

  return (
    <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-[rgba(232,103,90,0.18)] bg-[rgba(232,103,90,0.04)] p-4">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[rgba(232,103,90,0.8)]">
        Relationship with {firstName}
      </p>

      <div className="grid grid-cols-2 gap-3">
        <CadenceCell
          label={dynamics.cadenceLabel}
          daysSinceLast={dynamics.daysSinceLast}
          totalSessions={dynamics.totalSessions}
        />
        <DepthCell
          totalMinutes={dynamics.totalMinutes}
          signalCount={dynamics.signalCount}
        />
        <ToneCell
          dominant={dynamics.dominantSentiment}
          counts={dynamics.sentimentCounts}
        />
        <TrajectoryCell
          label={dynamics.trajectoryLabel}
          early={dynamics.earlySentiment}
          recent={dynamics.recentSentiment}
        />
      </div>
    </div>
  );
}

function CadenceCell({
  label,
  daysSinceLast,
  totalSessions,
}: {
  label: CadenceLabel | null;
  daysSinceLast: number | null;
  totalSessions: number;
}) {
  const cadencePhrase = label ? cadenceToPhrase(label) : "First conversation";
  const last =
    daysSinceLast === null
      ? null
      : daysSinceLast < 1
        ? "Today"
        : daysSinceLast < 2
          ? "Yesterday"
          : `${Math.round(daysSinceLast)}d ago`;

  return (
    <Cell
      icon={<CalendarClock className="h-3.5 w-3.5" />}
      label="Cadence"
      primary={cadencePhrase}
      secondary={
        last
          ? `${last} · ${totalSessions} session${totalSessions === 1 ? "" : "s"}`
          : `${totalSessions} session${totalSessions === 1 ? "" : "s"}`
      }
    />
  );
}

function DepthCell({
  totalMinutes,
  signalCount,
}: {
  totalMinutes: number;
  signalCount: number;
}) {
  const minutes = Math.max(1, Math.round(totalMinutes));
  return (
    <Cell
      icon={<Clock3 className="h-3.5 w-3.5" />}
      label="Depth"
      primary={`${minutes} min total`}
      secondary={
        signalCount === 0
          ? "No signals yet"
          : `${signalCount} thing${signalCount === 1 ? "" : "s"} learned`
      }
    />
  );
}

function ToneCell({
  dominant,
  counts,
}: {
  dominant: string | null;
  counts: Record<string, number>;
}) {
  if (!dominant) {
    return (
      <Cell
        icon={<Leaf className="h-3.5 w-3.5" />}
        label="Tone"
        primary="Not scored yet"
        secondary={null}
      />
    );
  }
  const breakdown = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${v} ${k}`)
    .join(" · ");
  return (
    <Cell
      icon={<Leaf className="h-3.5 w-3.5" />}
      label="Tone"
      primary={`Mostly ${dominant}`}
      secondary={breakdown}
    />
  );
}

function TrajectoryCell({
  label,
  early,
  recent,
}: {
  label: TrajectoryLabel;
  early: string | null;
  recent: string | null;
}) {
  if (label === "new" || label === null) {
    return (
      <Cell
        icon={<Flame className="h-3.5 w-3.5" />}
        label="Trajectory"
        primary="New relationship"
        secondary={null}
      />
    );
  }
  const icon =
    label === "warming" ? (
      <TrendingUp className="h-3.5 w-3.5" />
    ) : label === "cooling" ? (
      <TrendingDown className="h-3.5 w-3.5" />
    ) : (
      <Flame className="h-3.5 w-3.5" />
    );
  const primary =
    label === "warming"
      ? "Warming"
      : label === "cooling"
        ? "Cooling"
        : "Steady";
  const secondary =
    early && recent ? `${early} → ${recent}` : "Consistent tone";
  return (
    <Cell icon={icon} label="Trajectory" primary={primary} secondary={secondary} />
  );
}

function Cell({
  icon,
  label,
  primary,
  secondary,
}: {
  icon: React.ReactNode;
  label: string;
  primary: string;
  secondary: string | null;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)] p-3">
      <div className="flex items-center gap-1.5 text-[rgba(232,103,90,0.75)]">
        {icon}
        <span className="text-[10px] uppercase tracking-[0.15em] text-[rgba(255,255,255,0.4)]">
          {label}
        </span>
      </div>
      <p className="text-[14px] font-medium text-[rgba(255,255,255,0.9)]">
        {primary}
      </p>
      {secondary && (
        <p className="text-[11px] text-[rgba(255,255,255,0.45)]">{secondary}</p>
      )}
    </div>
  );
}

function cadenceToPhrase(label: CadenceLabel): string {
  switch (label) {
    case "daily":
      return "Almost daily";
    case "weekly":
      return "About weekly";
    case "monthly":
      return "Monthly-ish";
    case "sparse":
      return "Occasionally";
    case "rare":
      return "Rarely";
  }
}
